/**
 * Shared client-side fetch/render for the FINATICS public dealer list.
 * Used by both /pages/wholesale ("Trusted by Fly Shops" strip — compact
 * mode) and /pages/dealer-locator (full cards, grouped by state).
 *
 * SPEC.md Change 3C / Change 1 "Trusted by Fly Shops in 10+ States":
 * dealer names are NOT hardcoded in the theme — they're rendered from the
 * same Bayou-sourced endpoint here so both pages stay in sync automatically
 * as Chad approves/removes dealers.
 *
 * Data source: GET https://bayou-app-production.up.railway.app/api/public/finatics-dealers
 * (SPEC.md Change 5A — public, read-only, no auth, CORS-restricted to
 * finaticsflyco.com).
 */
(function () {
  var ENDPOINT = 'https://bayou-app-production.up.railway.app/api/public/finatics-dealers';
  var CACHE_KEY = 'finatics_dealers_cache_v1';
  var CACHE_MS = 15 * 60 * 1000; // 15 minutes — matches Bayou's Cache-Control max-age

  function readCache() {
    try {
      var raw = sessionStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || !parsed.fetchedAt || Date.now() - parsed.fetchedAt > CACHE_MS) return null;
      return parsed.data;
    } catch (e) {
      return null;
    }
  }

  function writeCache(data) {
    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify({ fetchedAt: Date.now(), data: data }));
    } catch (e) {
      /* sessionStorage unavailable (private mode, quota) — fine, just skip caching */
    }
  }

  function fetchDealers() {
    var cached = readCache();
    if (cached) return Promise.resolve(cached);

    return fetch(ENDPOINT, { headers: { Accept: 'application/json' } })
      .then(function (res) {
        if (!res.ok) throw new Error('Bad response: ' + res.status);
        return res.json();
      })
      .then(function (json) {
        var dealers = Array.isArray(json.dealers) ? json.dealers : [];
        writeCache(dealers);
        return dealers;
      });
  }

  function groupByState(dealers) {
    var groups = {};
    dealers.forEach(function (d) {
      var key = d.state || 'Other';
      if (!groups[key]) groups[key] = [];
      groups[key].push(d);
    });
    var states = Object.keys(groups).sort();
    states.forEach(function (s) {
      groups[s].sort(function (a, b) { return (a.name || '').localeCompare(b.name || ''); });
    });
    return { states: states, groups: groups };
  }

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.textContent = str == null ? '' : String(str);
    return div.innerHTML;
  }

  function renderCompact(container, dealers) {
    if (!dealers.length) {
      container.innerHTML = '';
      return;
    }
    var byState = groupByState(dealers);
    var parts = byState.states.map(function (state) {
      var names = byState.groups[state].map(function (d) { return escapeHtml(d.name); }).join(', ');
      return '<strong>' + escapeHtml(state) + '</strong>: ' + names;
    });
    container.innerHTML = '<ul class="finatics-dealer-compact__list">' +
      parts.map(function (p) { return '<li>' + p + '</li>'; }).join('') +
      '</ul>';
  }

  function dealerCardHtml(d) {
    var addressLine = [d.street, [d.city, d.state].filter(Boolean).join(', '), d.zip].filter(Boolean).join(' &middot; ');
    var mapsQuery = encodeURIComponent([d.street, d.city, d.state, d.zip].filter(Boolean).join(', '));
    var directionsBtn = d.street
      ? '<a class="finatics-dealer-card__directions" href="https://www.google.com/maps/search/?api=1&query=' + mapsQuery + '" target="_blank" rel="noopener">Get Directions</a>'
      : '';
    var phoneLine = d.phone
      ? '<a class="finatics-dealer-card__phone" href="tel:' + escapeHtml(d.phone.replace(/[^\d+]/g, '')) + '">' + escapeHtml(d.phone) + '</a>'
      : '';
    var websiteLine = d.website
      ? '<a class="finatics-dealer-card__website" href="' + escapeHtml(d.website) + '" target="_blank" rel="noopener">Visit Website</a>'
      : '';
    return (
      '<li class="finatics-dealer-card">' +
        '<h3 class="finatics-dealer-card__name">' + escapeHtml(d.name) + '</h3>' +
        (addressLine ? '<p class="finatics-dealer-card__address">' + addressLine + '</p>' : '') +
        '<div class="finatics-dealer-card__meta">' + phoneLine + websiteLine + '</div>' +
        directionsBtn +
      '</li>'
    );
  }

  function renderCards(container, dealers) {
    var retailOrTier = dealers.filter(function (d) { return d.dealer_type !== 'online'; });
    var online = dealers.filter(function (d) { return d.dealer_type === 'online'; });

    if (!retailOrTier.length && !online.length) {
      container.innerHTML = '';
      return;
    }

    var byState = groupByState(retailOrTier);
    var html = byState.states.map(function (state) {
      return (
        '<div class="finatics-dealer-state-group">' +
          '<h2 class="finatics-dealer-state-group__heading">' + escapeHtml(state) + '</h2>' +
          '<ul class="finatics-dealer-card__grid">' +
            byState.groups[state].map(dealerCardHtml).join('') +
          '</ul>' +
        '</div>'
      );
    }).join('');

    if (online.length) {
      html += (
        '<div class="finatics-dealer-state-group finatics-dealer-state-group--online">' +
          '<h2 class="finatics-dealer-state-group__heading">Also Sold By</h2>' +
          '<ul class="finatics-dealer-card__grid">' +
            online.map(dealerCardHtml).join('') +
          '</ul>' +
        '</div>'
      );
    }

    container.innerHTML = html;
  }

  function render(container, opts) {
    if (!container) return;
    var mode = (opts && opts.mode) || 'cards';
    fetchDealers()
      .then(function (dealers) {
        if (!dealers.length) return; // leave existing fallback markup in place
        if (mode === 'compact') renderCompact(container, dealers);
        else renderCards(container, dealers);
        container.setAttribute('data-finatics-dealers-state', 'loaded');
      })
      .catch(function (err) {
        console.warn('FINATICS dealer list fetch failed, keeping static fallback:', err);
        container.setAttribute('data-finatics-dealers-state', 'fallback');
      });
  }

  window.FinaticsDealers = { render: render };
})();
